/*
  BRS size guard (facelift item 6)

  Craft/Dawn preselects the first available variant, so a shopper who never
  touches the Size picker orders size 4. This element, rendered inside
  <variant-selects>, clears the Size choice on a fresh page load (no ?variant=
  in the URL), disables Add to cart with a "Choose your size" label, and
  hands control back to Dawn's product-info.js the moment a size is picked.

  Quick-add modals need extra care. Picking a size re-renders product-info, which
  builds a fresh <brs-size-guard> and runs init() again. On a product page that is
  harmless because selecting a variant puts ?variant= in the URL and the check below
  bails out. A modal lives on the collection page and never changes the URL, so the
  guard used to re-arm on every re-render and clear the size the shopper had just
  picked. Add to cart then stayed stuck on "Choose your size" forever. The modal
  element itself survives those re-renders, so it carries the "already chosen" flag.
*/
if (!customElements.get('brs-size-guard')) {
  customElements.define(
    'brs-size-guard',
    class BrsSizeGuard extends HTMLElement {
      connectedCallback() {
        // Defer one tick so sibling fieldsets are in the DOM.
        requestAnimationFrame(() => this.init());
      }

      init() {
        const variantSelects = this.closest('variant-selects');
        const productInfo = this.closest('product-info');
        if (!variantSelects || !productInfo) return;
        if (new URL(window.location.href).searchParams.has('variant')) return;

        // Survives the re-render that a variant change triggers inside a modal.
        const modal = this.closest('quick-add-modal');
        if (modal && modal.dataset.brsSizeChosen === '1') return;

        const optionName = (this.dataset.optionName || 'size').toLowerCase();
        const fieldset = Array.from(variantSelects.querySelectorAll('fieldset')).find((fs) => {
          const legend = fs.querySelector('legend');
          return legend && legend.textContent.trim().toLowerCase().startsWith(optionName);
        });
        if (!fieldset) return;

        const radios = Array.from(fieldset.querySelectorAll('input[type="radio"]'));
        if (radios.length < 2) return;

        radios.forEach((r) => (r.checked = false));
        fieldset.classList.add('brs-size-guard--unselected');

        /* A collection page carries one quick-add modal per product, so ids repeat and
           a document-wide lookup can disable a different product's form. Scope to this
           product-info instead. Note there are two /cart/add forms inside it and the
           first has no Add button, so go through <product-form> rather than taking the
           first form that matches, or the guard silently protects the wrong one. */
        const productForm = productInfo.querySelector('product-form');
        const form =
          (productForm && productForm.querySelector('form[action*="/cart/add"]')) ||
          document.getElementById(`product-form-${productInfo.dataset.section}`);
        const idInput = form && form.querySelector('input[name="id"]');
        if (idInput) idInput.disabled = true;
        if (productForm && typeof productForm.toggleSubmitButton === 'function') {
          productForm.toggleSubmitButton(true, this.dataset.label || 'Choose your size');
        }

        const release = () => {
          fieldset.classList.remove('brs-size-guard--unselected');
          if (idInput) idInput.disabled = false;
          if (modal) modal.dataset.brsSizeChosen = '1';
          if (productForm && typeof productForm.toggleSubmitButton === 'function') {
            productForm.toggleSubmitButton(false);
          }
          radios.forEach((r) => r.removeEventListener('change', release));
        };
        radios.forEach((r) => r.addEventListener('change', release));
      }
    }
  );
}
